/**
 * Wave 26.10.9/26.10.10/26.10.11 — Tile Homescreen with per-user drag-and-drop layout.
 *
 * Wave 26.10.11: defensive fallback. Owner should NEVER see "no tiles
 * available for your role" — the API may fail or return empty in transient
 * edge cases. We render a static safety-net set of tiles so the user is
 * never stranded.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ClipboardList, LifeBuoy, CheckSquare, Users, Wrench,
  Sparkles, Package, MessageSquare, TrendingUp, Receipt, Truck,
  LayoutDashboard, Image as ImageIcon, Camera as CameraIcon, Briefcase, ReceiptText,
  Loader2, RefreshCw, ChevronRight, Pencil, Check,
  Eye, EyeOff, GripVertical, AlertTriangle, Clock, ScanText,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const ICONS = {
  ClipboardList, LifeBuoy, CheckSquare, Users, Wrench,
  Sparkles, Package, MessageSquare, TrendingUp, Receipt, Truck,
  LayoutDashboard, ImageIcon, Camera: CameraIcon, Briefcase, ReceiptText, Clock, ScanText,
};

// Static safety-net tiles — rendered if /api/home/tiles fails or returns
// empty. Ensures owner/admin/manager are NEVER stranded.
const FALLBACK_TILES = [
  { key: "dashboard", label: "Dashboard", icon: "LayoutDashboard", route: "/dashboard", accent: "#1FB6C1", stats: [] },
  { key: "jobcards", label: "Job Cards", icon: "ClipboardList", route: "/jobcards", accent: "#1FB6C1", stats: [] },
  { key: "clocking", label: "Clocking", icon: "Clock", route: "/clock", accent: "#1FB6C1", stats: [] },
  { key: "team", label: "Team", icon: "Users", route: "/team", accent: "#1FB6C1", stats: [] },
  { key: "tickets", label: "Support Tickets", icon: "LifeBuoy", route: "/tickets", accent: "#F43F5E", stats: [] },
  { key: "customers", label: "Customers", icon: "Users", route: "/customers", accent: "#A78BFA", stats: [] },
  { key: "equipment", label: "Equipment", icon: "Wrench", route: "/equipment", accent: "#A78BFA", stats: [] },
  { key: "inbox", label: "Chat", icon: "MessageSquare", route: "/chat", accent: "#22C55E", stats: [] },
  { key: "gallery", label: "Gallery", icon: "ImageIcon", route: "/gallery", accent: "#A78BFA", stats: [] },
  { key: "camera", label: "Camera", icon: "Camera", route: "/camera", accent: "#1FB6C1", stats: [] },
  // 2026-02-18 Bug #1 (Dirk — quick receipt upload from home for techs).
  // All-roles tile that drops slips into a receipt_inbox queue for
  // admin to allocate to project expenses.
  { key: "receipt", label: "Receipt scan", icon: "ReceiptText", route: "/receipt-scan", accent: "#F59E0B", stats: [] },
  // Iter 169 — Slip scanner (delivery / fuel / expense slips, auto-OCR).
  { key: "slips", label: "Slip scanner", icon: "ScanText", route: "/slips", accent: "#F59E0B", stats: [] },
  { key: "engineering", label: "Engineering Desk", icon: "Sparkles", route: "/tools/engineering-desk", accent: "#1FB6C1", stats: [] },
];

// ── Layout persistence helpers ────────────────────────────────────────
const LS_ORDER_KEY = "hv_home_tile_order";
const LS_HIDDEN_KEY = "hv_home_tile_hidden";

const loadOrder = () => {
  try {
    const raw = localStorage.getItem(LS_ORDER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_e) { return null; }
};
const loadHidden = () => {
  try {
    const raw = localStorage.getItem(LS_HIDDEN_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch (_e) { return new Set(); }
};
const persistLayout = (order, hidden) => {
  try {
    localStorage.setItem(LS_ORDER_KEY, JSON.stringify(order));
    localStorage.setItem(LS_HIDDEN_KEY, JSON.stringify([...hidden]));
  } catch (_e) { /* ignore */ }
  api.patch("/users/me/preferences", {
    home_tile_order: order,
    home_tile_hidden: [...hidden],
  }).catch(() => {});
};

export default function Home() {
  const { user } = useAuth();
  const [data, setData] = useState({ tiles: [], role: "", is_tech: false });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [order, setOrder] = useState(() => loadOrder() || []);
  const [hidden, setHidden] = useState(() => loadHidden());

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const { data: payload } = await api.get("/home/tiles");
      // Wave 26.10.11 — if the server returned no tiles for any reason
      // (mid-deploy, error swallowed, etc.) fall back to the static set.
      // [Iter 290zp.19] Also defend against payload.tiles being a
      // non-array (null / object) — common cause of the rep-side
      // "filter is not a function" production crash.
      const tiles = Array.isArray(payload?.tiles) ? payload.tiles : null;
      if (!tiles || tiles.length === 0) {
        setData({ tiles: FALLBACK_TILES, role: payload?.role || "", is_tech: !!payload?.is_tech });
      } else {
        setData({ ...payload, tiles });
      }
      setLoadError(null);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("home tiles load failed", e);
      setLoadError(e?.response?.status ? `Error ${e.response.status}` : "Connection error");
      // Owner / admin / manager should ALWAYS see at least the safety-net tiles.
      setData((prev) => prev.tiles.length > 0 ? prev : { tiles: FALLBACK_TILES, role: "", is_tech: false });
    } finally {
      if (!silent) setLoading(false); else setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 60_000);
    return () => clearInterval(t);
  }, [load]);

  // Pull server-side layout pref on first user load (cross-device sync).
  useEffect(() => {
    if (!user?.id) return;
    const remoteOrder = user?.preferences?.home_tile_order;
    const remoteHidden = user?.preferences?.home_tile_hidden;
    if (Array.isArray(remoteOrder) && remoteOrder.length && !loadOrder()) {
      setOrder(remoteOrder);
    }
    if (Array.isArray(remoteHidden) && remoteHidden.length && !localStorage.getItem(LS_HIDDEN_KEY)) {
      setHidden(new Set(remoteHidden));
    }
  }, [user]);

  // Sort tiles by saved order (unknown keys go to the end), filter hidden in view mode.
  const orderedTiles = useMemo(() => {
    // [Iter 290zp.19] Defensive — guard against `data.tiles` being a
    // non-array (corrupt/legacy state, transient API hiccup).
    const tiles = Array.isArray(data?.tiles) ? data.tiles : [];
    const byKey = new Map(tiles.map((t) => [t.key, t]));
    const result = [];
    // First: tiles in the saved order
    for (const k of order) {
      if (byKey.has(k)) {
        result.push(byKey.get(k));
        byKey.delete(k);
      }
    }
    // Then: any remaining tiles (newly added by the backend) appended.
    for (const t of byKey.values()) result.push(t);
    return result;
  }, [data.tiles, order]);

  const visibleTiles = editing ? orderedTiles : (Array.isArray(orderedTiles) ? orderedTiles : []).filter((t) => !hidden.has(t.key));

  const moveTile = (fromKey, toKey) => {
    if (fromKey === toKey) return;
    const keys = orderedTiles.map((t) => t.key);
    const fi = keys.indexOf(fromKey);
    const ti = keys.indexOf(toKey);
    if (fi === -1 || ti === -1) return;
    const next = keys.slice();
    next.splice(fi, 1);
    next.splice(ti, 0, fromKey);
    setOrder(next);
    persistLayout(next, hidden);
  };

  const toggleHidden = (key) => {
    const next = new Set(hidden);
    if (next.has(key)) next.delete(key); else next.add(key);
    setHidden(next);
    persistLayout(orderedTiles.map((t) => t.key), next);
  };

  const resetLayout = () => {
    const defaultOrder = data.tiles.map((t) => t.key);
    setOrder(defaultOrder);
    setHidden(new Set());
    persistLayout(defaultOrder, new Set());
  };

  const greeting = (() => {
    const hr = new Date().getHours();
    if (hr < 12) return "Morning";
    if (hr < 18) return "Afternoon";
    return "Evening";
  })();

  return (
    <div className="space-y-5" data-testid="home-tiles-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold">
            {greeting}, {user?.name?.split(" ")[0] || "there"}
          </h1>
          <p className="text-xs text-[#647087] mt-0.5">
            {editing
              ? "Drag tiles to reorder, tap the eye to hide. Tap Done when finished."
              : data.is_tech
                ? "Your day at a glance — tap a tile to dive in."
                : "Your team at a glance — tap a tile to dive in."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {editing && (
            <button
              type="button"
              onClick={resetLayout}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="home-reset-layout"
              title="Reset tiles to default order"
            >
              Reset
            </button>
          )}
          <button
            type="button"
            onClick={() => setEditing((v) => !v)}
            className={`hv-btn text-xs ${editing ? "hv-btn-primary" : "hv-btn-ghost"}`}
            data-testid="home-customise-toggle"
            title={editing ? "Done customising" : "Customise tile order & visibility"}
          >
            {editing ? <Check className="w-3.5 h-3.5" /> : <Pencil className="w-3.5 h-3.5" />}
            {editing ? "Done" : "Customise"}
          </button>
          <button
            type="button"
            onClick={() => load(true)}
            disabled={refreshing}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="home-refresh"
            title="Refresh tile counts"
            aria-label="Refresh"
          >
            {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="hv-card p-4 h-[140px] animate-pulse bg-white/[0.02]" />
          ))}
        </div>
      ) : visibleTiles.length === 0 ? (
        <div className="hv-card p-8 text-center text-[#647087] text-sm space-y-3" data-testid="home-empty-state">
          <AlertTriangle className="w-8 h-8 mx-auto opacity-40" />
          {hidden.size > 0 ? (
            <>
              <div>All tiles are currently hidden.</div>
              <div className="flex justify-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => { const empty = new Set(); setHidden(empty); persistLayout(orderedTiles.map((t) => t.key), empty); }}
                  className="hv-btn hv-btn-primary text-xs"
                  data-testid="home-restore-all-tiles"
                >
                  Show all tiles
                </button>
                <Link to="/dashboard" className="hv-btn hv-btn-ghost text-xs">Open Dashboard</Link>
              </div>
            </>
          ) : (
            <>
              <div>Couldn't load your Home tiles right now.</div>
              <div className="flex justify-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => load(false)}
                  className="hv-btn hv-btn-primary text-xs"
                  data-testid="home-empty-retry"
                >
                  <RefreshCw className="w-3.5 h-3.5" /> Retry
                </button>
                <Link to="/dashboard" className="hv-btn hv-btn-ghost text-xs">Open Dashboard</Link>
                <Link to="/jobcards" className="hv-btn hv-btn-ghost text-xs">Open Job Cards</Link>
              </div>
            </>
          )}
        </div>
      ) : (
        <>
          {loadError && (
            <div className="hv-card border-amber-400/30 bg-amber-500/5 p-2 text-xs text-amber-200 flex items-center gap-2" data-testid="home-load-warning">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
              <span>Live counters unavailable ({loadError}). Showing default tiles — tap any to navigate.</span>
              <button onClick={() => load(false)} className="ml-auto underline whitespace-nowrap" data-testid="home-warning-retry">Retry</button>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2" data-testid="home-tile-grid">
            {visibleTiles.map((tile) => (
              <Tile
                key={tile.key}
                tile={tile}
                editing={editing}
                isHidden={hidden.has(tile.key)}
                onToggleHidden={() => toggleHidden(tile.key)}
                onMoveBefore={(srcKey) => moveTile(srcKey, tile.key)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Tile({ tile, editing, isHidden, onToggleHidden, onMoveBefore }) {
  const Icon = ICONS[tile.icon] || ClipboardList;
  const accent = tile.accent || "#1FB6C1";
  const ref = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleDragStart = (e) => {
    if (!editing) return;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/hv-tile", tile.key);
  };
  const handleDragOver = (e) => {
    if (!editing) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOver(true);
  };
  const handleDragLeave = () => setDragOver(false);
  const handleDrop = (e) => {
    if (!editing) return;
    e.preventDefault();
    setDragOver(false);
    const src = e.dataTransfer.getData("text/hv-tile");
    if (src) onMoveBefore(src);
  };

  // In edit mode the card is NOT a Link (drag/click conflicts) — it's a div
  // with explicit hide/show + drag handle controls.
  const Wrap = editing ? "div" : Link;
  const wrapProps = editing ? {} : { to: tile.route };

  // Wave 26.10.13 — info-dense horizontal layout. No aspect-square (which
  // wasted huge vertical space on tablet). Instead: fixed h-24 with icon+label
  // top, all 3 stats inline at the bottom. ~2× the data density.
  const stats = (tile.stats || []).slice(0, 3);
  const primary = stats[0];

  return (
    <Wrap
      ref={ref}
      {...wrapProps}
      draggable={editing}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`hv-card !p-2 group transition-colors flex flex-col relative h-[88px] sm:h-[96px] ${
        editing
          ? `cursor-move ${dragOver ? "border-[#1FB6C1] bg-[#1FB6C1]/10" : "border-dashed border-white/15"} ${isHidden ? "opacity-50" : ""}`
          : "hover:border-[#1FB6C1]/50"
      }`}
      data-testid={`home-tile-${tile.key}`}
      style={{ "--tile-accent": accent }}
    >
      {/* Top row: icon + label + (edit controls / chevron) */}
      <div className="flex items-center gap-1.5 min-w-0">
        {editing && (
          <GripVertical className="w-3 h-3 text-[#647087] shrink-0" aria-hidden="true" />
        )}
        <div
          className="w-7 h-7 rounded flex items-center justify-center shrink-0 border"
          style={{ background: `${accent}1a`, borderColor: `${accent}55`, color: accent }}
        >
          <Icon style={{ width: 14, height: 14 }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-[12px] text-white leading-tight truncate">{tile.label}</div>
          {primary && (
            <div className="text-[8px] uppercase tracking-[0.1em] text-[#647087] truncate">
              {primary.label}
            </div>
          )}
        </div>
        {editing ? (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleHidden(); }}
            className="text-[#647087] hover:text-[#1FB6C1] p-0.5 shrink-0"
            data-testid={`home-tile-toggle-${tile.key}`}
            title={isHidden ? "Show this tile" : "Hide this tile"}
            aria-label={isHidden ? "Show" : "Hide"}
          >
            {isHidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          </button>
        ) : null}
      </div>

      {/* Bottom row: big primary number + 2 secondary stats inline */}
      <div className="mt-auto flex items-end justify-between gap-1.5">
        {primary && (
          <div
            className="font-display font-bold text-2xl leading-none"
            style={{ color: primary.accent || accent }}
          >
            {Number.isFinite(primary.value) ? primary.value : "—"}
          </div>
        )}
        {stats.length > 1 && (
          <div className="flex gap-1 items-end pb-0.5">
            {stats.slice(1).map((s, i) => (
              <span
                key={i}
                className="text-[9px] font-mono px-1 py-0 rounded-sm border whitespace-nowrap leading-tight"
                style={{
                  borderColor: `${s.accent || accent}33`,
                  background: `${s.accent || accent}0a`,
                  color: s.accent || accent,
                }}
                title={`${s.label}: ${s.value}`}
              >
                {s.value}
              </span>
            ))}
          </div>
        )}
      </div>
    </Wrap>
  );
}

// Stat component no longer used in compact tiles; kept for reference / future
// large-tile variants.
